/**
 * CursoSupervisor - Página de detalle del curso
 * Supervisor de Seguridad Privada
 * Modalidad: 100% Online
 */

import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
    Clock,
    Laptop,
    Users,
    Award,
    CheckCircle2,
    ArrowRight,
    ClipboardList,
    MessageSquare,
    Settings,
    Shield,
    Eye,
    Handshake,
    BookOpen,
    ChevronDown,
    GraduationCap,
    Calendar,
    Globe
} from 'lucide-react';
import { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';

// Funciones del supervisor
const funciones = [
    {
        icon: Users,
        title: 'Supervisar al Personal',
        description: 'Dirigir a los guardias asignados, asegurando el cumplimiento de tareas y procedimientos de seguridad.',
    },
    {
        icon: ClipboardList,
        title: 'Planificación y Asignación',
        description: 'Determinar puntos clave de seguridad y distribuir el personal de manera eficiente.',
    },
    {
        icon: BookOpen,
        title: 'Registro e Informes',
        description: 'Llevar registro de actividades, incidentes y generar informes detallados para la gerencia.',
    },
    {
        icon: GraduationCap,
        title: 'Capacitación del Personal',
        description: 'Entrenar al personal nuevo y existente en procedimientos, equipos y resolución de conflictos.',
    },
    {
        icon: Settings,
        title: 'Control de Equipos',
        description: 'Asegurar el correcto funcionamiento de cámaras, alarmas y equipos de comunicación.',
    },
    {
        icon: Handshake,
        title: 'Coordinación',
        description: 'Interactuar con gerencia, recursos humanos y proveedores de servicios de seguridad.',
    },
];

// Lo que aprenderás
const aprendizajes = [
    {
        icon: BookOpen,
        title: 'Conocimientos Teóricos',
        description: 'Base sólida en seguridad privada, leyes vigentes, ética profesional y legislación.',
    },
    {
        icon: Users,
        title: 'Liderazgo y Supervisión',
        description: 'Técnicas para supervisar guardias, coordinar equipos y tomar decisiones informadas.',
    },
    {
        icon: Shield,
        title: 'Buenas Prácticas',
        description: 'Implementación de procedimientos efectivos, gestión de crisis y prevención del delito.',
    },
    {
        icon: Eye,
        title: 'Prevención de Riesgos',
        description: 'Gestión de guardias, protocolos vigentes y uso seguro de elementos de protección.',
    },
    {
        icon: MessageSquare,
        title: 'Comunicación Efectiva',
        description: 'Habilidades para entregar órdenes claras y transparentes al personal de seguridad.',
    },
    {
        icon: Award,
        title: 'Ética Profesional',
        description: 'Gestión con profesionalismo, puntualidad y supervisión constante.',
    },
];

// Módulos del curso
const modulos = [
    'Legislación',
    'Administración de la Seguridad',
    'Seguridad en Instalaciones',
    'Prevención de Riesgos y Seguridad Industrial',
    'Sistemas de Alertas',
    'Primeros Auxilios',
    'Oratoria e Imagen Personal',
    'Autocuidados en Seguridad Privada',
    'Resolución de Conflictos',
    'Evaluación Final',
];

// Requisitos
const requisitosChilenos = [
    'Cédula de identidad vigente por ambos lados',
    'Certificado de estudios: 4to medio o 4to medio laboral',
    'Certificado de antecedentes para fines especiales',
    'Correo electrónico para acceso a plataforma',
];

const requisitosExtranjeros = [
    'Residencia definitiva (NO en trámite)',
    'Estudios apostillados y validados en Chile (equivalente a 4to medio)',
    'Certificado de antecedentes para fines especiales',
    'Correo electrónico para acceso a plataforma',
];

// FAQ
const faqs = [
    {
        pregunta: '¿Cuándo comienzan las clases?',
        respuesta: 'El curso es 100% online y puede comenzar todos los días, de Lunes a Domingo.',
    },
    {
        pregunta: '¿En cuánto tiempo puedo completar el curso?',
        respuesta: 'Tienes 30 días corridos desde la entrega de tus credenciales para finalizar el curso.',
    },
    {
        pregunta: '¿Con qué nota se aprueba?',
        respuesta: 'El curso se aprueba con una nota igual o superior a 4.0 (escala de 1.0 a 7.0).',
    },
    {
        pregunta: '¿Cómo obtengo mi credencial?',
        respuesta: 'Al completar el curso recibirás un certificado y concentración de notas. La credencial es gestionada por la empresa que te contrate.',
    },
    {
        pregunta: '¿Puedo pagar en cuotas?',
        respuesta: 'Sí, puedes pagar el 50% al inicio del curso y el resto al finalizar. Para mayor información, consulta directamente en nuestro WhatsApp.',
    },
];

// Componente FAQ Item
function FAQItem({ pregunta, respuesta }) {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="border border-white/10 rounded-xl overflow-hidden"
        >
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full p-5 flex items-center justify-between bg-white/5 hover:bg-white/10 transition-colors text-left"
            >
                <span className="font-semibold text-white">{pregunta}</span>
                <ChevronDown className={`w-5 h-5 text-violet-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>
            {isOpen && (
                <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    className="p-5 bg-white/5 border-t border-white/10"
                >
                    <p className="text-slate-400">{respuesta}</p>
                </motion.div>
            )}
        </motion.div>
    );
}

export default function CursoSupervisor() {
    const [activeTab, setActiveTab] = useState('chilenos');

    return (
        <div className="min-h-screen bg-slate-900">
            <Header />

            {/* Hero Section */}
            <section className="relative pt-32 pb-20 overflow-hidden">
                {/* Background Image */}
                <div className="absolute inset-0">
                    <img
                        src="/Images/supr.webp"
                        alt="Supervisor de Seguridad"
                        className="w-full h-full object-cover opacity-40"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-slate-900/70 via-slate-900/90 to-slate-900" />
                </div>

                {/* Decorative elements */}
                <div className="absolute inset-0 overflow-hidden">
                    <div className="absolute -top-40 -right-40 w-80 h-80 bg-violet-500/20 rounded-full blur-3xl" />
                    <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-500/20 rounded-full blur-3xl" />
                </div>

                <div className="container mx-auto px-4 relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="max-w-4xl"
                    >
                        {/* Badges */}
                        <div className="flex flex-wrap gap-3 mb-6">
                            <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-slate-600/90 text-white text-sm font-bold rounded-full border border-slate-500">
                                No disponible por el momento
                            </span>
                            <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-violet-500/20 text-violet-300 text-sm font-medium rounded-full">
                                <Clock className="w-4 h-4" />
                                140 horas
                            </span>
                            <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-purple-500/20 text-purple-300 text-sm font-medium rounded-full">
                                <Globe className="w-4 h-4" />
                                100% Online
                            </span>
                            <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-fuchsia-500/20 text-fuchsia-300 text-sm font-medium rounded-full">
                                <Laptop className="w-4 h-4" />
                                Plataforma 24/7
                            </span>
                        </div>

                        <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 font-heading leading-tight">
                            Curso de{' '}
                            <span className="bg-gradient-to-r from-violet-400 to-purple-400 bg-clip-text text-transparent">
                                Supervisor de Seguridad Privada
                            </span>
                        </h1>

                        <p className="text-xl text-slate-300 mb-8 leading-relaxed max-w-3xl">
                            <span className="block mb-4 text-amber-300 font-semibold">
                                ⚠️ Este curso no está disponible temporalmente. Estamos trabajando para ofrecértelo pronto.
                            </span>
                            Desarrolla las habilidades de liderazgo necesarias para coordinar, dirigir y
                            supervisar equipos de seguridad. Curso 100% online con contenido actualizado
                            según la normativa de seguridad privada vigente (Ley 21.659).
                        </p>

                        {/* Price and CTA */}
                        <div className="flex flex-wrap items-center gap-6">
                            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 opacity-60">
                                <p className="text-slate-400 text-sm mb-1">Inversión</p>
                                <p className="text-4xl font-bold text-white">$100.000 <span className="text-lg text-slate-400">CLP</span></p>
                            </div>
                            <motion.button
                                disabled
                                className="inline-flex items-center gap-2 px-8 py-4 bg-slate-600 text-slate-300 font-bold rounded-xl shadow-lg cursor-not-allowed opacity-60"
                            >
                                Curso no disponible temporalmente
                            </motion.button>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Funciones del Supervisor */}
            <section className="py-20 bg-white">
                <div className="container mx-auto px-4">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <span className="inline-block px-4 py-1.5 bg-violet-100 text-violet-600 text-sm font-medium rounded-full mb-4">
                            Rol Profesional
                        </span>
                        <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4 font-heading">
                            ¿Qué hace un Supervisor de Seguridad?
                        </h2>
                        <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                            El supervisor es el jefe de guardias, responsable de que todos los puestos estén cubiertos
                        </p>
                    </motion.div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
                        {funciones.map((funcion, index) => {
                            const Icon = funcion.icon;
                            return (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: index * 0.1 }}
                                    whileHover={{ y: -8 }}
                                    className="p-6 rounded-2xl bg-gradient-to-br from-slate-50 to-violet-50 border border-slate-100"
                                >
                                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center mb-5">
                                        <Icon className="w-7 h-7 text-white" />
                                    </div>
                                    <h3 className="text-xl font-bold text-slate-800 mb-3">{funcion.title}</h3>
                                    <p className="text-slate-600">{funcion.description}</p>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Lo que Aprenderás */}
            <section className="py-20 bg-slate-900">
                <div className="container mx-auto px-4">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <span className="inline-block px-4 py-1.5 bg-violet-500/20 text-violet-400 text-sm font-medium rounded-full mb-4">
                            Objetivos del Curso
                        </span>
                        <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 font-heading">
                            Lo que{' '}
                            <span className="bg-gradient-to-r from-violet-400 to-purple-400 bg-clip-text text-transparent">
                                Aprenderás
                            </span>
                        </h2>
                        <p className="text-lg text-slate-400 max-w-2xl mx-auto">
                            Tendrás todas las herramientas necesarias para desempeñarte profesionalmente
                        </p>
                    </motion.div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                        {aprendizajes.map((item, index) => {
                            const Icon = item.icon;
                            return (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: index * 0.05 }}
                                    className="p-6 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 transition-all"
                                >
                                    <div className="flex items-start gap-4">
                                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center flex-shrink-0">
                                            <Icon className="w-6 h-6 text-white" />
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-white mb-2">{item.title}</h3>
                                            <p className="text-slate-400 text-sm">{item.description}</p>
                                        </div>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Módulos */}
            <section className="py-20 bg-slate-50">
                <div className="container mx-auto px-4">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <span className="inline-block px-4 py-1.5 bg-violet-100 text-violet-600 text-sm font-medium rounded-full mb-4">
                            Programa de Estudios
                        </span>
                        <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4 font-heading">
                            Módulos del Curso
                        </h2>
                        <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                            Cada módulo incluye videos, material PDF, trabajos y evaluación
                        </p>
                    </motion.div>

                    <div className="max-w-4xl mx-auto">
                        <div className="grid md:grid-cols-2 gap-4">
                            {modulos.map((modulo, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: index * 0.05 }}
                                    className="flex items-center gap-4 p-4 bg-white rounded-xl shadow-md border border-slate-100"
                                >
                                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center text-white font-bold">
                                        {index + 1}
                                    </div>
                                    <span className="text-slate-700 font-medium">{modulo}</span>
                                </motion.div>
                            ))}
                        </div>

                        {/* Info adicional */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            className="mt-12 grid md:grid-cols-3 gap-6"
                        >
                            <div className="text-center p-6 bg-white rounded-2xl shadow-lg border border-slate-100">
                                <Clock className="w-8 h-8 text-violet-500 mx-auto mb-3" />
                                <p className="text-2xl font-bold text-slate-800">140 horas</p>
                                <p className="text-slate-500">Asincrónico</p>
                            </div>
                            <div className="text-center p-6 bg-white rounded-2xl shadow-lg border border-slate-100">
                                <Calendar className="w-8 h-8 text-violet-500 mx-auto mb-3" />
                                <p className="text-2xl font-bold text-slate-800">30 días</p>
                                <p className="text-slate-500">Para completar</p>
                            </div>
                            <div className="text-center p-6 bg-white rounded-2xl shadow-lg border border-slate-100">
                                <Laptop className="w-8 h-8 text-violet-500 mx-auto mb-3" />
                                <p className="text-2xl font-bold text-slate-800">24/7</p>
                                <p className="text-slate-500">Acceso a plataforma</p>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Requisitos */}
            <section className="py-20 bg-white">
                <div className="container mx-auto px-4">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-12"
                    >
                        <span className="inline-block px-4 py-1.5 bg-violet-100 text-violet-600 text-sm font-medium rounded-full mb-4">
                            Documentación
                        </span>
                        <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4 font-heading">
                            Requisitos de Inscripción
                        </h2>
                    </motion.div>

                    {/* Tabs */}
                    <div className="max-w-3xl mx-auto">
                        <div className="flex justify-center gap-4 mb-8">
                            <button
                                onClick={() => setActiveTab('chilenos')}
                                className={`px-6 py-3 rounded-xl font-semibold transition-all ${activeTab === 'chilenos'
                                    ? 'bg-gradient-to-r from-violet-500 to-purple-500 text-white shadow-lg shadow-violet-500/30'
                                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                    }`}
                            >
                                🇨🇱 Chilenos
                            </button>
                            <button
                                onClick={() => setActiveTab('extranjeros')}
                                className={`px-6 py-3 rounded-xl font-semibold transition-all ${activeTab === 'extranjeros'
                                    ? 'bg-gradient-to-r from-violet-500 to-purple-500 text-white shadow-lg shadow-violet-500/30'
                                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                    }`}
                            >
                                🌎 Extranjeros
                            </button>
                        </div>

                        <motion.div
                            key={activeTab}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="bg-slate-50 rounded-2xl p-8 border border-slate-100"
                        >
                            <ul className="space-y-4">
                                {(activeTab === 'chilenos' ? requisitosChilenos : requisitosExtranjeros).map((req, index) => (
                                    <li key={index} className="flex items-start gap-3">
                                        <CheckCircle2 className="w-6 h-6 text-violet-500 flex-shrink-0 mt-0.5" />
                                        <span className="text-slate-700">{req}</span>
                                    </li>
                                ))}
                            </ul>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="py-20 bg-slate-900">
                <div className="container mx-auto px-4">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-12"
                    >
                        <span className="inline-block px-4 py-1.5 bg-violet-500/20 text-violet-400 text-sm font-medium rounded-full mb-4">
                            Dudas Frecuentes
                        </span>
                        <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 font-heading">
                            Preguntas{' '}
                            <span className="bg-gradient-to-r from-violet-400 to-purple-400 bg-clip-text text-transparent">
                                Frecuentes
                            </span>
                        </h2>
                    </motion.div>

                    <div className="max-w-3xl mx-auto space-y-4">
                        {faqs.map((faq, index) => (
                            <FAQItem key={index} {...faq} />
                        ))}
                    </div>
                </div>
            </section>

            {/* Oportunidades Laborales */}
            <section className="py-20 bg-slate-50">
                <div className="container mx-auto px-4">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="max-w-4xl mx-auto"
                    >
                        <div className="text-center mb-12">
                            <span className="inline-block px-4 py-1.5 bg-violet-100 text-violet-600 text-sm font-medium rounded-full mb-4">
                                Campo Laboral
                            </span>
                            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4 font-heading">
                                ¿En qué puedo trabajar?
                            </h2>
                        </div>

                        <div className="grid md:grid-cols-3 gap-6">
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                className="p-6 bg-white rounded-2xl shadow-lg border border-slate-100 text-center"
                            >
                                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center mx-auto mb-4">
                                    <Users className="w-8 h-8 text-white" />
                                </div>
                                <h3 className="font-bold text-slate-800 mb-2">Supervisor</h3>
                                <p className="text-slate-600 text-sm">En empresas medianas o grandes</p>
                            </motion.div>

                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: 0.1 }}
                                className="p-6 bg-white rounded-2xl shadow-lg border border-slate-100 text-center"
                            >
                                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center mx-auto mb-4">
                                    <Shield className="w-8 h-8 text-white" />
                                </div>
                                <h3 className="font-bold text-slate-800 mb-2">Jefe de Grupo</h3>
                                <p className="text-slate-600 text-sm">Coordinador de equipos de seguridad</p>
                            </motion.div>

                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: 0.2 }}
                                className="p-6 bg-white rounded-2xl shadow-lg border border-slate-100 text-center"
                            >
                                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center mx-auto mb-4">
                                    <Settings className="w-8 h-8 text-white" />
                                </div>
                                <h3 className="font-bold text-slate-800 mb-2">Jefe de Operaciones</h3>
                                <p className="text-slate-600 text-sm">Con experiencia previa</p>
                            </motion.div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* CTA Final */}
            <section className="py-20 bg-gradient-to-br from-violet-600 via-purple-600 to-fuchsia-600">
                <div className="container mx-auto px-4">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center max-w-3xl mx-auto"
                    >
                        <GraduationCap className="w-16 h-16 text-white/80 mx-auto mb-6" />
                        <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 font-heading">
                            Curso Temporalmente No Disponible
                        </h2>
                        <p className="text-xl text-violet-100 mb-8">
                            Estamos trabajando para ofrecerte este curso pronto. Mientras tanto,
                            te invitamos a conocer nuestros otros programas de capacitación disponibles.
                        </p>

                        <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
                            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 opacity-60">
                                <p className="text-violet-100 text-sm mb-1">Inversión</p>
                                <p className="text-4xl font-bold text-white">$100.000 <span className="text-lg text-violet-200">CLP</span></p>
                            </div>
                            <Link to="/#cursos">
                                <motion.button
                                    whileHover={{ scale: 1.05, y: -2 }}
                                    whileTap={{ scale: 0.98 }}
                                    className="inline-flex items-center gap-2 px-10 py-5 bg-white text-violet-600 font-bold rounded-xl shadow-xl hover:bg-violet-50 transition-all text-lg"
                                >
                                    Ver Otros Cursos
                                    <ArrowRight className="w-6 h-6" />
                                </motion.button>
                            </Link>
                        </div>

                        <div className="flex flex-wrap justify-center gap-6 mt-10 text-violet-100">
                            <div className="flex items-center gap-2">
                                <Globe className="w-5 h-5" />
                                <span>100% Online</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Laptop className="w-5 h-5" />
                                <span>Plataforma 24/7</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Award className="w-5 h-5" />
                                <span>Certificación incluida</span>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            <Footer />
        </div>
    );
}
